"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const d3_scale_1 = require("d3-scale");
const withParentDimensions_1 = require("../generic/withParentDimensions");
const useDeepMemo_1 = require("../../hooks/useDeepMemo");
const lodash_1 = require("lodash");
const common_styled_components_1 = require("../common-styled-components");
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../theme");
const amountUtils_1 = require("../../util/amountUtils");
const react_1 = require("react");
const getSVGPoint_1 = require("../generic/getSVGPoint");
const Tooltip_1 = require("../generic/Tooltip");
const LimitLine = styled_components_1.default.line `
  stroke-dasharray: 5 2;
  stroke-width: 1px;
  fill: none;
`;
exports.default = (0, withParentDimensions_1.withParentDimensions)(({ data, limits, title, unit, parentDimensions: { width }, }) => {
    const HEIGHT = 200;
    const MARGINS = {
        top: 18,
        bottom: 20,
        left: 80,
        right: 25,
    };
    const CONTENT_BOTTOM = HEIGHT - MARGINS.bottom;
    const TICK_FONT_SIZE = 12;
    const svgRef = (0, react_1.useRef)(null);
    const [tooltipData, setTooltipData] = (0, react_1.useState)(undefined);
    const [scaleX, scaleSeries, scaleY, divider, sortedData, xValues, valueBySeriesX,] = (0, useDeepMemo_1.useDeepMemo)(() => {
        const sortedData = data.map((series) => ({
            ...series,
            data: series.data,
        }));
        const xValues = (0, lodash_1.chain)(sortedData)
            .flatMap((series) => [
            ...series.data.map((point) => point.x),
            series.prediction?.x,
        ])
            .filter((x) => x !== undefined)
            .uniq()
            .value();
        const scaleX = (0, d3_scale_1.scaleBand)()
            .range([MARGINS.left, width - MARGINS.right])
            .domain(xValues)
            .paddingInner(0.2)
            .paddingOuter(0.05);
        const scaleSeries = (0, d3_scale_1.scaleBand)()
            .domain(sortedData.map((series) => series.name))
            .range([0, scaleX.bandwidth()])
            .paddingInner(0.12)
            .paddingOuter(0.06);
        const values = (0, lodash_1.flatMap)(sortedData, (series) => series.data
            .map((point) => point.value)
            .filter((value) => value !== undefined && !isNaN(value))
            .concat(series.prediction?.value ?? []));
        const limitValues = limits?.map((limit) => limit.value) ?? [];
        const allValues = values.concat(limitValues);
        const minY = allValues.length ? Math.min(...allValues, 0) : 0;
        const maxY = allValues.length ? Math.max(...allValues, 0) : 1;
        const paddedMinY = minY === maxY ? minY - 1 : minY;
        const paddedMaxY = minY === maxY ? maxY + 1 : maxY;
        const divider = (0, amountUtils_1.getDivider)(Math.max(Math.abs(paddedMinY), Math.abs(paddedMaxY))) ?? 1;
        const scaleY = (0, d3_scale_1.scaleLinear)()
            .range([CONTENT_BOTTOM, MARGINS.top])
            .domain([paddedMinY, paddedMaxY])
            .nice();
        const valueBySeriesX = sortedData.reduce((result, series) => {
            const pointMap = series.data.reduce((mapResult, point) => {
                if (point.value === undefined || isNaN(point.value)) {
                    return mapResult;
                }
                return {
                    ...mapResult,
                    [point.x]: {
                        x: point.x,
                        value: point.value,
                        isPrediction: false,
                    },
                };
            }, {});
            if (series.prediction) {
                const predictionKey = series.prediction.x;
                if (!pointMap[predictionKey]) {
                    pointMap[predictionKey] = {
                        x: series.prediction.x,
                        value: series.prediction.value,
                        isPrediction: true,
                    };
                }
            }
            return {
                ...result,
                [series.name]: pointMap,
            };
        }, {});
        return [
            scaleX,
            scaleSeries,
            scaleY,
            divider,
            sortedData,
            xValues,
            valueBySeriesX,
        ];
    }, [data, limits, width]);
    const zeroLine = scaleY(0);
    const zeroLineInBounds = zeroLine >= MARGINS.top && zeroLine <= HEIGHT - MARGINS.bottom;
    const mouseMoved = (e) => {
        e.preventDefault();
        const svgPoint = (0, getSVGPoint_1.getSVGPoint)(e.clientX, e.clientY, svgRef);
        if (!svgPoint || !xValues.length) {
            setTooltipData(undefined);
            return;
        }
        const nearestX = (0, lodash_1.minBy)(xValues, (xValue) => {
            const x = scaleX(xValue);
            if (x === undefined) {
                return Number.POSITIVE_INFINITY;
            }
            const center = x + scaleX.bandwidth() / 2;
            return Math.abs(center - svgPoint.x);
        });
        if (!nearestX) {
            setTooltipData(undefined);
            return;
        }
        const pointsForX = sortedData.map((series) => {
            const point = valueBySeriesX[series.name]?.[nearestX];
            return {
                series: series.name,
                color: series.color,
                label: nearestX,
                value: point?.value,
                isPrediction: point?.isPrediction ?? false,
            };
        });
        const selectedGroupX = scaleX(nearestX);
        if (selectedGroupX === undefined) {
            setTooltipData(undefined);
            return;
        }
        const nextTooltipData = {
            point: new DOMPoint(selectedGroupX + scaleX.bandwidth() / 2, svgPoint.y),
            containerSize: {
                width,
                height: HEIGHT,
            },
            data: pointsForX.map((point) => ({
                series: point.series,
                color: point.color,
                label: point.label,
                value: point.value,
            })),
            isPrediction: pointsForX.some((point) => point.value !== undefined) &&
                pointsForX
                    .filter((point) => point.value !== undefined)
                    .every((point) => point.isPrediction),
        };
        setTooltipData(nextTooltipData);
    };
    const mouseOut = (e) => {
        e.preventDefault();
        setTooltipData(undefined);
    };
    const datesPerTick = Math.max(1, Math.ceil(xValues.length / Math.max(1, Math.round(width / 120))));
    return ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.GraphTitle, { children: title }), (0, jsx_runtime_1.jsxs)("div", { style: { position: "relative" }, children: [(0, jsx_runtime_1.jsxs)("svg", { width: width, height: HEIGHT, onMouseMove: mouseMoved, onMouseOut: mouseOut, ref: svgRef, children: [xValues.map((xValue, index) => {
                                if (index % datesPerTick !== 0 && index !== xValues.length - 1) {
                                    return undefined;
                                }
                                const tickX = scaleX(xValue);
                                if (tickX === undefined) {
                                    return undefined;
                                }
                                const centerX = tickX + scaleX.bandwidth() / 2;
                                const tickLength = 6;
                                return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: centerX, x2: centerX, y1: CONTENT_BOTTOM, y2: CONTENT_BOTTOM + tickLength, strokeWidth: 1, stroke: "#aaa" }), (0, jsx_runtime_1.jsx)("text", { x: centerX, textAnchor: "middle", y: CONTENT_BOTTOM + tickLength + TICK_FONT_SIZE, fontSize: TICK_FONT_SIZE, children: xValue })] }, xValue));
                            }), scaleY.ticks(4).map((tick) => {
                                const tickY = scaleY(tick);
                                const tickLength = 6;
                                return ((0, jsx_runtime_1.jsxs)("g", { children: [(0, jsx_runtime_1.jsx)("line", { x1: width - MARGINS.right, x2: MARGINS.left - tickLength, y1: tickY, y2: tickY, strokeWidth: 1, stroke: theme_1.theme.colors.grey(4), className: "stroke-gray-200" }), (0, jsx_runtime_1.jsxs)("text", { x: MARGINS.left - tickLength, textAnchor: "end", y: tickY + TICK_FONT_SIZE / 2 - 2, fontSize: TICK_FONT_SIZE, children: [(0, amountUtils_1.formatNumber)(tick / divider), (0, amountUtils_1.getDividerLabel)(divider)] })] }, tick.toString()));
                            }), zeroLineInBounds && ((0, jsx_runtime_1.jsx)("line", { strokeWidth: 1, stroke: "#aaa", x1: MARGINS.left, x2: width - MARGINS.right, y1: zeroLine, y2: zeroLine })), (0, jsx_runtime_1.jsx)("line", { strokeWidth: 1, stroke: "#aaa", x1: MARGINS.left, x2: MARGINS.left, y1: CONTENT_BOTTOM, y2: 0 }), (0, jsx_runtime_1.jsx)("text", { x: MARGINS.left + 4, y: MARGINS.top - 3, fontSize: TICK_FONT_SIZE, children: unit }), limits?.map((limit) => ((0, jsx_runtime_1.jsx)(LimitLine, { x1: MARGINS.left, x2: width - MARGINS.right, y1: scaleY(limit.value), y2: scaleY(limit.value), stroke: limit.color }, limit.label))), xValues.map((xValue) => {
                                const groupX = scaleX(xValue);
                                if (groupX === undefined) {
                                    return undefined;
                                }
                                return ((0, jsx_runtime_1.jsx)("g", { children: sortedData.map((series) => {
                                        const point = valueBySeriesX[series.name]?.[xValue];
                                        if (!point) {
                                            return undefined;
                                        }
                                        const barX = groupX + (scaleSeries(series.name) ?? 0);
                                        const barWidth = scaleSeries.bandwidth();
                                        const startY = scaleY(0);
                                        const endY = scaleY(point.value);
                                        return ((0, jsx_runtime_1.jsx)("rect", { x: barX, width: barWidth, y: Math.min(startY, endY), height: Math.max(1, Math.abs(endY - startY)), fill: series.color, opacity: point.isPrediction ? 0.45 : 0.85, stroke: series.color, strokeDasharray: point.isPrediction || series.dashed
                                                ? "3 2"
                                                : undefined, strokeWidth: point.isPrediction ? 1 : 0, rx: 1 }, `${series.name}-${xValue}`));
                                    }) }, xValue));
                            })] }), tooltipData && (0, jsx_runtime_1.jsx)(Tooltip_1.Tooltip, { tooltipData: tooltipData })] }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Legend, { children: [data.map((series) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.LegendItem, { color: series.color, text: series.name }, series.name))), limits?.map((limit) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.LegendItem, { color: limit.color, text: limit.label }, limit.label)))] })] }));
});
